import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_VIEW_URL,
  Entity,
  EntityRelation,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
} from '@backstage/catalog-model';
import {
  CodeSnippet,
  Table,
  TableColumn,
  TableProps,
  WarningPanel,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  getEntityRelations,
  humanizeEntityRef,
  useEntityList,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';

import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { TextField, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import Edit from '@material-ui/icons/Edit';
import OpenInNew from '@material-ui/icons/OpenInNew';
import Star from '@material-ui/icons/Star';
import StarBorder from '@material-ui/icons/StarBorder';
import _debounce from 'lodash.debounce';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { getSystemsByGroupName } from '../../services/systems.service';
import { columnFactories } from './columns';
import { CatalogTableRow } from './types';
import { GetEntitiesResponse } from '@backstage/catalog-client';
import useCustomEntities from '../../hooks/useEntities';
import { contentpages, notification } from '../../utils/constants';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';

const JourneySteps = [
  'Inspiration & Planning',
  'Compare & Select',
  'Book Travel',
  'Plan & Prepare',
  'Get to the airport & await flight',
  'Take flight',
  'Connect',
  'Arrive at the destination',
  'Get to and stay at destination',
  'Stay involved',
  'Help',
];
const journeyStepIndex = (entity: any) => {
  if (entity.kind === 'Domain') {
    const step = entity.metadata?.title ?? '';
    return JourneySteps.indexOf(step);
  }
  return -1;
};
/**
 * Props for {@link CatalogTable}.
 *
 * @public
 */
export interface CatalogTableProps {
  columns?: TableColumn<CatalogTableRow>[];
  actions?: TableProps<CatalogTableRow>['actions'];
  tableOptions?: TableProps<CatalogTableRow>['options'];
  emptyContent?: ReactNode;
  subtitle?: string;
  isOwned?: boolean;
  isAll?: boolean;
  setSelected?: (response: string) => void;
  handleIsService?: (response: boolean) => boolean;
  handleIsBusinessComponentSelected?: (value: boolean) => boolean;
  SelectedBusinessComponentType?: string;
  handleRefreshSelection?: () => void;
  businessComponentData?: {
    notificationList: [];
    contentTopics: [];
  };
  productListEntity?: Entity[];
  selectedProduct_Topic?: string[];
  setSelectedFilter?: (selected: string) => void;
}

interface EntityRelationTarget {
  kind: string;
  name: string;
  namespace: string;
}
export interface CustomEntityRelation extends EntityRelation {
  target?: EntityRelationTarget;
}

const YellowStar = withStyles({
  root: {
    color: '#f3ba37',
  },
})(Star);

const refCompare = (a: Entity, b: Entity) => {
  const toRef = (entity: Entity) =>
    entity?.metadata?.title ||
    humanizeEntityRef(entity, {
      defaultKind: 'Component',
    });

  return toRef(a).localeCompare(toRef(b));
};

export async function getSystemByByName(id: string) {
  const res: any[] = [];
  const systemArr = await getSystemsByGroupName(id);
  if (systemArr && systemArr.length > 0)
    for (const data of systemArr) {
      const r = data ? JSON.parse(JSON.stringify(data)).processed_entity : null;

      const response = {
        apiVersion: r ? JSON.parse(r).apiVersion : null,
        kind: r ? JSON.parse(r).kind : null,
        spec: r ? JSON.parse(r).spec : null,
        metadata: r ? JSON.parse(r).metadata : null,
      };
      res.push(response);
    }
  return res;
}

function sanitizeUrlParams(urlParams: URLSearchParams) {
  const params: Record<string, any> = Object.create(null);
  const disallowedKeys = ['__proto__', 'constructor', 'prototype'];

  for (const [key, value] of urlParams.entries()) {
    const match = key.match(/^(\w+)\[(\w+)\]$/);
    if (match) {
      const [, mainKey, subKey] = match;
      if (disallowedKeys.includes(mainKey) || disallowedKeys.includes(subKey))
        continue;
      params[mainKey] = params[mainKey] || Object.create(null);
      params[mainKey][subKey] = value;
    } else {
      if (disallowedKeys.includes(key)) continue;
      params[key] = value;
    }
  }

  return params;
}

export function handleUrlSearchParams() {
  if (!window.location.search) return null;

  const urlParams = new URLSearchParams(window.location.search);

  const safeParams = sanitizeUrlParams(urlParams);

  return safeParams;
}

export const CustomCatalogTable = (props: CatalogTableProps) => {
  const catalogApi = useApi(catalogApiRef);
  const isService_ = window?.location?.search
    ?.split('kind%5D=')[1]
    ?.includes('system');
  const { columns, actions, tableOptions, subtitle, emptyContent } = props;
  props.handleIsService?.(isService_!!);
  const { isStarredEntity, toggleStarredEntity } = useStarredEntities();
  const { loading, error, entities, filters } = useEntityList();
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchParam, setSearchParam] = useState('');
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [productLinks, setProductLinks] = useState<string[]>([]);
  const [entity, setEntityList] = useState<Entity[]>([]);
  const { selectedKindLabel } = useSelector((state:RootState)=>state.globalState);
  const customSearchEntities = ['component','user', 'group', 'businesscomponent'];
  const customEntities = ['platform', 'coe', 'valuestream'];
  const businessComponentTypes = [notification, contentpages];
  const isBusinessComponents =
    filters?.kind?.value?.toLowerCase() === 'businesscomponent';
  props.setSelectedFilter?.(filters.kind?.value!);
  const isNotificationSelected =
    isBusinessComponents &&
    props.SelectedBusinessComponentType === notification;
  const isContentedPagesSelected =
    isBusinessComponents &&
    props.SelectedBusinessComponentType === contentpages;

  const isCapabilitySelected =
    filters?.kind?.value?.toLowerCase() === 'capability';

  const navigate = useNavigate();

  const { getCustomEntitiesByKind } = useCustomEntities('Component');
  const isCopVsPLT = customEntities?.some(entity =>
    entity?.includes(filters?.kind?.value?.toLowerCase() as string),
  );
  props.handleIsBusinessComponentSelected?.(isBusinessComponents);
  const getgroupEntities =
    useCallback(async (): Promise<GetEntitiesResponse> => {
      const data = await catalogApi.getEntities({
        filter: { kind: 'Group' },
      });
      return data;
    }, [catalogApi]);

  const filterBusinessComponents = useCallback(async () => {
    const entities = (await getCustomEntitiesByKind())?.items || [];

    const filteredComponents = entities.filter(x => {
      const type = x.spec?.type?.toString()?.toLowerCase();
      if (!type) {
        console.warn('Missing type for entity', x.metadata?.name);
        return false;
      }
      return businessComponentTypes.some(z => type.includes(z.toLowerCase()));
    });
    setEntityList(filteredComponents);
  }, [businessComponentTypes]);

  useEffect(() => {
    if (window.location.search) {
      const params = handleUrlSearchParams();

      if (params?.products) {
        setProductLinks(params?.products?.split(','));
      }
    }
    if (isCopVsPLT) {
      getgroupEntities()
        .then(res => res.items)
        .then(items => {
          setEntityList(
            items?.filter(x => {
              const type = x?.spec?.type?.toString()?.toLowerCase();
              const filterKind = filters?.kind?.value?.toLowerCase();

              if (filterKind === 'valuestream') {
                return type === 'value stream';
              }
              return type === filterKind;
            }),
          );
        })
        .catch(err => console.error(err));
    }

    if (isBusinessComponents) {
      filterBusinessComponents();
    }

    if (isCapabilitySelected && props.selectedProduct_Topic?.length!! > 0) {
      const data = handleCapabilityFiltering();
      setEntityList(data);
    }

    props?.handleRefreshSelection?.();
  }, [filters?.kind]);

  function handleCapabilityFiltering() {
    const { selectedProduct_Topic } = props;
    const filtered = entities.filter(x => {
      const products = x?.spec?.products as string[] | undefined;
      return products?.some(y => selectedProduct_Topic?.includes(y));
    });

    return filtered;
  }

  function handleBusinessComponentFiltering() {
    const { selectedProduct_Topic } = props;
    const filteredEntityResult: Entity[] = [];

    if (selectedProduct_Topic && selectedProduct_Topic.length > 0) {
      for (const entityFilter of selectedProduct_Topic) {
        const filtered = entity.find(x => {
          const products = x?.spec?.products as string[] | undefined;
          const topic = x?.spec?.topic as string | undefined;

          if (isNotificationSelected) {
            // Check if any product matches
            return (
              products?.some(y => y === entityFilter) &&
              x.spec?.type === notification
            );
          } else {
            // Compare topic ignoring case
            return (
              topic?.toLowerCase() === entityFilter.toLowerCase() &&
              x.spec?.type === contentpages
            );
          }
        });

        if (filtered) filteredEntityResult.push(filtered as Entity);
      }
    } else {
      if (isContentedPagesSelected) {
        return entity?.filter(x => x.spec?.type === contentpages);
      } else if (isNotificationSelected) {
        return entity?.filter(x => x.spec?.type === notification);
      } else {
        return entity as Entity[];
      }
    }
    return filteredEntityResult;
  }

  useEffect(() => {
    const params = handleUrlSearchParams() as any;
    const kind = params?.filters?.kind;
    if (
      props.selectedProduct_Topic?.length!! > 0 ||
      props.SelectedBusinessComponentType
    ) {
      if (
        isBusinessComponents &&
        (isContentedPagesSelected || isNotificationSelected)
      ) {
        navigate(
          `catalog?filters%5Bkind%5D=businesscomponent&filters%5Buser%5D=all&type=${props.SelectedBusinessComponentType as string}&selectedTopics=${props.selectedProduct_Topic}`,
        );
      }
      if (
        kind?.toLowerCase() === 'capability' &&
        props.selectedProduct_Topic?.length!! > 0
      ) {
        navigate(
          `catalog?filters%5Bkind%5D=capability&filters%5Buser%5D=all&selectedProducts=${props.selectedProduct_Topic}`,
        );
      } else {
        navigate(
          `catalog?filters%5Bkind%5D=${kind ?? 'domain'}&filters%5Buser%5D=all`,
        );
      }
    }
  }, [props.SelectedBusinessComponentType, props.selectedProduct_Topic]);

  const defaultColumns: TableColumn<CatalogTableRow>[] = useMemo(() => {
    return [
      columnFactories.createTitleColumn({ hidden: true }),
      columnFactories.createNameColumn({ defaultKind: filters.kind?.value }),
      columnFactories.createMetadataDescriptionColumn(),
      ...createEntitySpecificColumns(),
      columnFactories.createTagsColumn(filters.kind?.value),
    ];
  }, [filters.kind, entities]);

  function createCapabilitySpecificColumns(): TableColumn<CatalogTableRow>[] {
    return [
      columnFactories.createNameColumn(),
      columnFactories.createMetadataDescriptionColumn(),
      
    ];
  }

  function createBusinessComponentsSpecificColumns(): TableColumn<CatalogTableRow>[] {
    const baseColumns = [
      columnFactories.createSystemColumn(),
      columnFactories.createOwnerColumn(filters?.kind?.value),
      columnFactories.createSpecTypeColumn(),
      columnFactories.createSpecLifecycleColumn(),
    ];
    if (
      isBusinessComponents &&
      !isContentedPagesSelected &&
      !isNotificationSelected
    ) {
      return [
        columnFactories.createNameColumn(),
        columnFactories.createMetadataDescriptionColumn(),
        columnFactories.createBusinessComponentColumn(),
      ];
    }
    if (isContentedPagesSelected) {
      return [
        columnFactories.createContentPageNameColumn(),
        columnFactories.createContentPageTopicColumn(),
        columnFactories.createContentPageContentManagerColumn(),
      ];
    }
    if (isNotificationSelected) {
      return [
        columnFactories.createNameColumn(),
        columnFactories.createMetadataDescriptionColumn(),
        columnFactories.createContactPersonColumn(),
      ];
    }

    return baseColumns;
  }

  function createEntitySpecificColumns(): TableColumn<CatalogTableRow>[] {
    const baseColumns = [
      columnFactories.createSystemColumn(),
      columnFactories.createOwnerColumn(filters?.kind?.value),
      columnFactories.createSpecTypeColumn(),
      columnFactories.createSpecLifecycleColumn(),
    ];
    switch (filters.kind?.value) {
      case 'user':
        return [];
      case 'domain':
      case 'system':
      case 'capability':
        return [];
      case 'group':
        return [];
      case 'coe':
        return [
          columnFactories.createNameColumn(),
          columnFactories.createMetadataDescriptionColumn(),
          columnFactories.createTagsColumn(),
        ];
      case 'platform':
        return [
          columnFactories.createNameColumn(),
          columnFactories.createMetadataDescriptionColumn(),
          columnFactories.createTagsColumn(),
        ];
      case 'valuestream':
        return [
          columnFactories.createNameColumn(),
          columnFactories.createMetadataDescriptionColumn(),
          columnFactories.createTagsColumn(),
        ];
      case 'template':
        return [columnFactories.createSpecTypeColumn()];
      case 'location':
        return [
          columnFactories.createSpecTypeColumn(),
          columnFactories.createSpecTargetsColumn(),
        ];
      default:
        return entities.every(entity => entity.metadata.namespace === 'default')
          ? baseColumns
          : [...baseColumns, columnFactories.createNamespaceColumn()];
    }
  }
  if (error) {
    return (
      <div>
        <WarningPanel
          severity="error"
          title="Could not fetch catalog entities."
        >
          <CodeSnippet language="text" text={error.toString()} />
        </WarningPanel>
      </div>
    );
  }

  const defaultActions: TableProps<CatalogTableRow>['actions'] = [
    ({ entity }) => {
      const url = entity.metadata.annotations?.[ANNOTATION_VIEW_URL];
      const title = 'View';

      return {
        icon: () => (
          <>
            <Typography variant="srOnly">{title ?? ''}</Typography>
            <OpenInNew fontSize="small" />
          </>
        ),
        tooltip: title ?? '',
        disabled: !url,
        onClick: () => {
          if (!url) return;
          window.open(url, '_blank');
        },
      };
    },
    ({ entity }) => {
      const url = entity.metadata.annotations?.[ANNOTATION_EDIT_URL];
      const title = 'Edit';

      return {
        icon: () => (
          <>
            <Typography variant="srOnly">{title ?? ''}</Typography>
            <Edit fontSize="small" />
          </>
        ),
        tooltip: title ?? '',
        disabled: !url,
        onClick: () => {
          if (!url) return;
          window.open(url, '_blank');
        },
      };
    },
    ({ entity }) => {
      const isStarred = isStarredEntity(entity);
      const title = isStarred ? 'Remove from favorites' : 'Add to favorites1';

      return {
        cellStyle: { paddingLeft: '1em' },
        icon: () => (
          <>
            <Typography variant="srOnly">{title ?? ''}</Typography>
            {isStarred ? <YellowStar /> : <StarBorder />}
          </>
        ),
        tooltip: title ?? '',
        onClick: () => toggleStarredEntity(entity),
      };
    },
  ];

  let rows = entities
    .filter(
      x =>
        !x.metadata.name?.toLowerCase().includes('_area_') &&
        x.metadata?.name !== 'lufthansa_group' &&
        x.metadata?.name !== 'dh_digitalhangarpeopleappgroups',
    )
    .sort((a, b) => {
      const aIndex = journeyStepIndex(a);
      const bIndex = journeyStepIndex(b);
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      return refCompare(a, b);
    })

    .filter(x => !x.metadata.name.endsWith('_contributors'))
    .map(entity => {
      const partOfSystemRelations = getEntityRelations(
        entity,
        RELATION_PART_OF,
        {
          kind: 'system',
        },
      );
      const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);

      return {
        entity,
        resolved: {
          // This name is here for backwards compatibility mostly; the
          // presentation of refs in the table should in general be handled with
          // EntityRefLink / EntityName components
          name: humanizeEntityRef(entity, {
            defaultKind: 'Component',
          }),
          entityRef: '',
          productList: props.productListEntity,
          ownedByRelations,
          partOfSystemRelationTitle: partOfSystemRelations
            .map(r =>
              humanizeEntityRef(r, {
                defaultKind: 'system',
              }),
            )
            .join(', '),
          partOfSystemRelations,
        },
      };
    });
  if (productLinks?.length > 0) {
    const filteredRows = rows.filter(x =>
      productLinks.some(link => x.entity.metadata.name === link),
    );
    if (filteredRows.length > 0) {
      rows = filteredRows;
    }
  }

  if (isCopVsPLT && entity && entity?.length > 0) {
    handleContentRows(entity);
  }

  if (isCapabilitySelected && props.selectedProduct_Topic?.length!! > 0) {
    const filteredCapabilities = entities.filter(x => {
      const products = x?.spec?.products as string[] | undefined;

      return products?.some(y => props.selectedProduct_Topic?.includes(y));
    });

    handleContentRows(filteredCapabilities);
  }

  function handleContentRows(_entity: Entity[]) {
    rows = [];
    rows = _entity.map(entity => {
      const partOfSystemRelations = getEntityRelations(
        entity,
        RELATION_PART_OF,
        { kind: 'component' }, // filter for group entities instead of system
      );
      const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);
      const contacts = entity?.spec?.contacts as [];
      return {
        entity,
        resolved: {
          name: entity.metadata.title as string,
          entityRef: `component:default/${entity.metadata.name}`,
          topic: entity?.spec?.topic,
          productList: props.productListEntity,
          product: entity?.spec?.products,
          manager: contacts?.find(
            (x: { role: string }) =>
              x.role?.toLowerCase() === 'content manager' ||
              x?.role?.toLowerCase() === 'contact person',
          ),
          ownedByRelations,
          partOfSystemRelationTitle: []
            .map(r => humanizeEntityRef(r, { defaultKind: 'group' }))
            .join(', '),
          partOfSystemRelations,
        },
      };
    });
  }
  if (isBusinessComponents && entity) {
    handleContentRows(entity);
  }

  if (isContentedPagesSelected && props.businessComponentData?.contentTopics) {
    const filteredData = handleBusinessComponentFiltering();
    handleContentRows(
      filteredData ?? props.businessComponentData?.contentTopics,
    );
  }

  if (isNotificationSelected && props.businessComponentData?.notificationList) {
    const filteredData = handleBusinessComponentFiltering();
    handleContentRows(
      filteredData ?? props.businessComponentData?.notificationList,
    );
  }
  const showPagination = rows.length > 20;

  /* eslint-disable */
  const executeSearch = (query: string) => {
    const isSearchByGroup = customSearchEntities.some(x =>
      filters.kind?.value?.includes(x),
    );
    const filteredData = rows.filter(x => {
      if (isSearchByGroup) {
        const displayName = (
          x.entity?.spec?.profile as any
        )?.displayName?.toLowerCase();
        const description = x.entity.metadata.description?.toLowerCase();
        const title = x.entity.metadata.title?.toLowerCase();
        return (
          displayName?.includes(query?.toLowerCase()) ||
          description?.includes(query?.toLowerCase()) ||
          title?.includes(query?.toLowerCase())||
          x.entity?.metadata?.name?.toLowerCase()?.includes(query?.toLowerCase())
        );
      }
      return x.entity.metadata.name.includes(query);
    });
    setSearchResults(filteredData);
    setIsSearchLoading(false);
  };

  const debouncedSearch = _debounce(executeSearch, 500);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchParam(value);
    if (!value) {
      setSearchParam('');
    } else {
      debouncedSearch(value);
    }
  };

  const label =
  filters.kind?.value === 'system'
    ? 'Product'
    : filters.kind?.value === 'domain'
      ? 'Journey Step'
      : filters.kind?.value === 'businesscomponent' &&
          !isContentedPagesSelected &&
          !isNotificationSelected
        ? 'Business Component'
        : isContentedPagesSelected
          ? 'Content Page'
          : isNotificationSelected
            ? 'Notification'
            : filters.kind?.value?.toLowerCase() === 'coe'
              ? 'CoE'
              : selectedKindLabel ?? filters.kind?.value;

const pluralLabel = label?.endsWith('y')
  ? `${label.slice(0, -1)}ies`
  : `${label}s`;

  

  return (
    <div style={{ position: 'relative' }}>
      <section>
        {customSearchEntities.includes(filters.kind?.value as string) && (
          <TextField
            variant="standard"
            placeholder="Filter"
            value={searchParam}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchParam && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      setSearchParam('');
                      setSearchResults(rows);
                    }}
                    size="small"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              disableUnderline: false,
            }}
            style={{
              top: '16px',
              position: 'absolute',
              zIndex: 1,
              right: '36px',
            }}
          />
        )}
      </section>
      <Table
        isLoading={loading || isSearchLoading}
        columns={
          isBusinessComponents || isContentedPagesSelected
            ? createBusinessComponentsSpecificColumns()
            : isCopVsPLT
              ? createEntitySpecificColumns()
              : isCapabilitySelected
                ? createCapabilitySpecificColumns()
                : columns || defaultColumns
        }
        options={{
          paging: showPagination,
          
          pageSize: 20, 
          actionsColumnIndex: -1,
          loadingType: 'linear',
          search: !customSearchEntities.includes(filters.kind?.value as string),
          showEmptyDataSourceMessage: false,
          padding: 'dense',
          pageSizeOptions: [20, 50, 100],
          ...tableOptions,
        }}
        initialState={{
          filtersOpen: true,
        }}  
        title={`${pluralLabel} (${
          filters.kind?.value === 'system'
            ? rows.length
            : isCopVsPLT
              ? rows?.length
              : isCapabilitySelected
                ? rows.length
                : isBusinessComponents
                  ? rows?.length
                  : entities
                      .filter(
                        x => !x.metadata.name?.toLowerCase().includes('_area_'),
                      )
                      .filter(x => !x.metadata.name.endsWith('_contributors'))
                      .length
        })`}
        data={isCopVsPLT ? rows : searchParam ? searchResults : rows}
        initialFormData={{
          FormData,
        }}
        actions={
          (isBusinessComponents||isCapabilitySelected)
            ? []
            : !isContentedPagesSelected
              ? actions || defaultActions
              : actions || defaultActions
        }
        subtitle={subtitle}
        emptyContent={emptyContent}
      />
    </div>
  );
};
CustomCatalogTable.columns = columnFactories;
